import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Activity, TrendingDown, TrendingUp, Minus, Flame, Lock, Info } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Display config by level
const LEVEL = {
  green:  { label: "Calm",     bar: "#4FB3A5", bg: "#EAF6F4", text: "#146B5F" },
  yellow: { label: "Tense",    bar: "#E9A13B", bg: "#FFF3DC", text: "#8B4A00" },
  red:    { label: "High-risk",bar: "#E07A7A", bg: "#FEE4E4", text: "#8B2D2D" },
};

function TrendIcon({ trend, className }) {
  if (trend === "improving") return <TrendingDown size={14} className={className} />;
  if (trend === "worsening") return <TrendingUp size={14} className={className} />;
  return <Minus size={14} className={className} />;
}

function trendLabel(trend) {
  if (trend === "improving") return "Calmer than last week";
  if (trend === "worsening") return "Tenser than last week";
  return "Steady";
}

/**
 * Conflict Score card — shown on Home tab for users who have at least 1 analysis.
 * - Free users see a preview with a PRO teaser.
 * - PRO users see the full score + distribution + streak.
 */
export default function ConflictScoreCard({ isPro = false, onUpgradeClick }) {
  const { authHeaders, isAuthenticated } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/user/conflict-score?days=7`, { headers: authHeaders() })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [isAuthenticated, authHeaders]);

  if (loading) return null;
  // Hide card entirely until user has at least 1 analysis
  if (!data || data.total_analyses === 0) return null;

  const lvl = LEVEL[data.level] || LEVEL.green;
  const score = data.score;
  const pctBar = score !== null ? Math.min(100, (score / 10) * 100) : 0;

  return (
    <div
      className="bg-white rounded-2xl shadow-soft border border-[#E5E7EB] overflow-hidden"
      data-testid="conflict-score-card"
    >
      {/* Header */}
      <div className="p-4 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-[#EAF6F4] flex items-center justify-center">
            <Activity size={16} className="text-[#4FB3A5]" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-[#1F3A5F]">Your Conflict Score</h3>
            <p className="text-[10px] text-[#A0A7B4] font-medium uppercase tracking-wider">
              Last 7 days · {data.current_window.count} {data.current_window.count === 1 ? "check" : "checks"}
            </p>
          </div>
        </div>
        {!isPro && (
          <div className="inline-flex items-center gap-1 text-[10px] font-bold text-[#B4762D] uppercase tracking-wider">
            <Lock size={10} />
            PRO preview
          </div>
        )}
      </div>

      {/* Score */}
      <div className="px-4 pb-3">
        <div className="flex items-baseline gap-2 mb-1.5">
          <span className="text-3xl font-bold text-[#1F3A5F]" data-testid="conflict-score-value">
            {score !== null ? score.toFixed(1) : "—"}
          </span>
          <span className="text-xs text-[#6B7280]">/ 10</span>
          <span
            className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full"
            style={{ backgroundColor: lvl.bg, color: lvl.text }}
            data-testid="conflict-score-level"
          >
            {lvl.label}
          </span>
        </div>

        {/* Progress bar */}
        <div className="h-2 rounded-full bg-[#F3F4F6] overflow-hidden mb-3" aria-label="Conflict score bar">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${pctBar}%`, backgroundColor: lvl.bar }}
          />
        </div>

        {/* Trend */}
        <div className="flex items-center gap-1.5 text-[11px] text-[#6B7280]" data-testid="conflict-score-trend">
          <TrendIcon
            trend={data.trend}
            className={
              data.trend === "improving"
                ? "text-[#4FB3A5]"
                : data.trend === "worsening"
                ? "text-[#E07A7A]"
                : "text-[#A0A7B4]"
            }
          />
          <span>{trendLabel(data.trend)}</span>
          {data.streak_days > 1 && isPro && (
            <span className="ml-auto inline-flex items-center gap-1 text-[#E9A13B]">
              <Flame size={11} />
              {data.streak_days}-day streak
            </span>
          )}
        </div>
      </div>

      {/* PRO full view / locked */}
      {isPro ? (
        <div className="bg-[#F7F9FB] border-t border-[#E5E7EB] px-4 py-3">
          <div className="grid grid-cols-3 gap-2 text-center">
            {["green", "yellow", "red"].map((lev) => {
              const c = LEVEL[lev];
              const count = data.distribution[lev] || 0;
              return (
                <div key={lev} className="rounded-xl p-2" style={{ backgroundColor: c.bg }}>
                  <div className="text-base font-bold" style={{ color: c.text }}>
                    {count}
                  </div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: c.text }}>
                    {c.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <button
          onClick={onUpgradeClick}
          data-testid="conflict-score-upgrade"
          className="w-full bg-gradient-to-r from-[#1F3A5F] via-[#2a4a75] to-[#4FB3A5] text-white px-4 py-3 flex items-center justify-between hover:brightness-110 active:scale-[0.99] transition-all"
        >
          <span className="flex items-center gap-2 text-[12px] font-semibold">
            <Flame size={13} className="text-[#FFD27F]" />
            Unlock trends, streaks &amp; history with PRO
          </span>
          <span className="text-[11px] font-bold bg-white/15 px-2 py-1 rounded-full">
            {`$5.99/yr →`}
          </span>
        </button>
      )}
    </div>
  );
}
